import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, type ReferencedChunk, type Wiki, type WikiSection } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { fmtKstFull } from '../lib/time';
import { ErrorBox } from './Watchlist';

/**
 * Citation tag preprocessor: `[chunk_id]` 또는 `[id1, id2]` → 마크다운 링크.
 * format:
 *   - old: `${reportId}:${idx}` 예: `6a3dccaef39f9f62:0`
 *   - new: `${reportId}:${sourceType}:${idx}` 예: `6a3dccaef39f9f62:text:0`, `:vision:2`
 * 출력: `[¹](#cite-<chunk_id>)` 형태. components.a 가 인터셉트해서 hover tooltip 렌더.
 * 같은 chunk_id 등장 시 #1, #2 같은 번호 부여 (페이지별 카운트).
 */
function preprocessCitations(md: string): string {
  const idMap = new Map<string, number>();
  const idPattern = '[a-f0-9]{8,}(?::(?:text|vision))?:\\d+';
  const re = new RegExp(`\\[((?:${idPattern})(?:,\\s*(?:${idPattern}))*)\\]`, 'g');
  return md.replace(re, (_, raw) => {
    const ids = raw.split(/,\s*/);
    const marks = ids.map((id: string) => {
      if (!idMap.has(id)) idMap.set(id, idMap.size + 1);
      const n = idMap.get(id)!;
      const sup = String(n)
        .split('')
        .map((d) => '⁰¹²³⁴⁵⁶⁷⁸⁹'[Number(d)])
        .join('');
      return `[${sup}](#cite-${id})`;
    });
    return marks.join('');
  });
}

function CitationMark({
  chunkId,
  chunk,
  children,
}: {
  chunkId: string;
  chunk: ReferencedChunk | undefined;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block">
      <sup
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="cursor-pointer text-blue-600 hover:text-blue-800"
      >
        {children}
      </sup>
      {open && (
        <div className="absolute bottom-full left-1/2 z-50 mb-1 block w-96 max-w-[90vw] -translate-x-1/2 rounded border border-slate-200 bg-white p-3 text-left text-xs font-normal normal-case shadow-lg">
          {chunk ? (
            <>
              <div className="mb-1.5 text-[11px] text-slate-500">
                {chunk.broker ?? '미상'} · {chunk.title}
                {chunk.published_at ? ` · ${chunk.published_at.slice(0, 10)}` : ''}
                {chunk.page_number !== null ? ` · p.${chunk.page_number}` : ''}
                {chunk.source_type === 'vision' && (
                  <span className="ml-1 rounded bg-orange-100 px-1 text-[10px] text-orange-700">vision</span>
                )}
              </div>
              <div className="prose prose-sm prose-slate max-h-96 max-w-none overflow-y-auto text-slate-800 prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1.5 prose-table:my-2 prose-th:px-1.5 prose-th:py-0.5 prose-td:px-1.5 prose-td:py-0.5 prose-li:my-0">
                <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>{chunk.text}</ReactMarkdown>
              </div>
            </>
          ) : (
            <div className="text-slate-500">청크 정보 없음 ({chunkId})</div>
          )}
        </div>
      )}
    </span>
  );
}

const SECTION_LABELS: Record<string, string> = {
  tldr: 'TL;DR',
  thesis: '투자 논리',
  catalysts: '카탈리스트',
  broker_views: '증권사 의견',
  recent: '최근 변화',
  financials: '재무',
  valuation: '밸류에이션',
  segments: '사업 부문',
  competition: '경쟁 환경',
  risks: '리스크',
  business: '비즈니스 모델',
  open_questions: '미해결 질문',
  glossary: '용어',
};

const SECTION_ORDER = [
  'tldr',
  'thesis',
  'catalysts',
  'broker_views',
  'recent',
  'financials',
  'valuation',
  'segments',
  'competition',
  'risks',
  'business',
  'open_questions',
  'glossary',
];

export default function WikiPage() {
  const { symbol } = useParams<{ symbol: string }>();
  const { data, error, loading } = useAsync<Wiki>(() => api.wiki(symbol!), [symbol]);
  if (error) return <ErrorBox msg={error} />;
  if (loading || !data) return <p className="text-slate-500">로딩 중…</p>;

  const sectionMap = new Map(data.sections.map((s: WikiSection) => [s.sectionKey, s]));
  const orderedSections = SECTION_ORDER.map((k) => ({ key: k, section: sectionMap.get(k) ?? null }));

  return (
    <section>
      <Header wiki={data} />
      <div className="mt-6 grid grid-cols-[180px_1fr] gap-8">
        <Toc orderedSections={orderedSections} />
        <Content
          symbol={symbol!}
          orderedSections={orderedSections}
          referencedChunks={data.referenced_chunks ?? {}}
          risks={data.risks ?? []}
          financials={data.financials ?? []}
          peerComparisons={data.peer_comparisons ?? []}
          operational={data.operational_metrics ?? []}
        />
      </div>
    </section>
  );
}

function Header({ wiki }: { wiki: Wiki }) {
  const c = wiki.header.consensus_target_price;
  const r = wiki.header.rating_distribution;
  // r.buy/hold/sell 가 이미 fraction (0-1, API getConsensus 에서 count/total 계산).
  // r.total 로 다시 나누면 N=3 일 때 1/3 → 33% 같은 잘못된 결과.
  const pct = (n: number) => Math.round(n * 100);
  return (
    <header className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex items-baseline gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{wiki.name}</h1>
        <span className="font-mono text-sm text-slate-500">{wiki.symbol}</span>
        <span className="ml-auto text-xs text-slate-500">{wiki.quarter ?? `v${wiki.symbol_version}`}</span>
        {wiki.freshness === 'updating' && (
          <span
            className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
            title="새 리포트를 반영 중입니다 — 다음 재계산에서 갱신됩니다"
          >
            갱신 중
          </span>
        )}
        {wiki.freshness === 'failed' && (
          <span
            className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800"
            title="위키 재계산이 실패했습니다 — 조치가 필요합니다"
          >
            갱신 실패
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm">
        <span>
          컨센서스{' '}
          <strong>{c.avg ? `${c.avg.toLocaleString()}원` : '—'}</strong>
          {c.count > 0 && (
            <span className="ml-1 text-slate-500">
              ({c.min?.toLocaleString() ?? '?'}-{c.max?.toLocaleString() ?? '?'}, N={c.count})
            </span>
          )}
        </span>
        <span>
          <span className="pill-buy">BUY {pct(r.buy)}%</span>{' '}
          <span className="pill-hold">HOLD {pct(r.hold)}%</span>{' '}
          <span className="pill-sell">SELL {pct(r.sell)}%</span>
        </span>
        {wiki.generated_at && (
          <span className="text-slate-500">
            업데이트 {fmtKstFull(wiki.generated_at)}
          </span>
        )}
      </div>
      {wiki.stock_data && <StockDataBar data={wiki.stock_data} />}
    </header>
  );
}

/**
 * Phase v3.5a — header 의 stock_data block.
 * 시가총액 / 외국인 / 거래량 / 52w 의 latest 보고서 의 snapshot.
 * 단위 변환: market_cap_bn (십억원) → 조원 의 표시 (1000 ÷).
 */
function StockDataBar({ data }: { data: NonNullable<Wiki['stock_data']> }) {
  const fmtTrillion = (n: number | null): string => {
    if (n === null) return '—';
    const trillions = n / 1000;
    if (trillions >= 100) return `${Math.round(trillions).toLocaleString()}조원`;
    return `${trillions.toFixed(1)}조원`;
  };
  const fmtPct = (n: number | null): string => (n === null ? '—' : `${n}%`);
  const fmtRaw = (n: number | null, suffix = ''): string =>
    n === null ? '—' : `${n.toLocaleString()}${suffix}`;
  // 발행주식 의 단위 의 일관성 추정: 백만 또는 천 (보고서 마다 다름). 그대로 표시.
  return (
    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-100 pt-2 text-xs text-slate-600">
      <span>
        <span className="text-slate-400">시가총액 </span>
        <strong className="text-slate-700">{fmtTrillion(data.market_cap_bn)}</strong>
      </span>
      <span>
        <span className="text-slate-400">외국인 </span>
        <strong className="text-slate-700">{fmtPct(data.foreign_pct)}</strong>
      </span>
      <span>
        <span className="text-slate-400">52w </span>
        <strong className="text-slate-700">
          {fmtRaw(data.low_52w, '원')} - {fmtRaw(data.high_52w, '원')}
        </strong>
      </span>
      {data.shares_outstanding_mn !== null && (
        <span>
          <span className="text-slate-400">발행주식 </span>
          <strong className="text-slate-700">{fmtRaw(data.shares_outstanding_mn)}</strong>
        </span>
      )}
      <span className="ml-auto text-slate-400">📊 D1 ({data.dated_at})</span>
    </div>
  );
}

function Toc({
  orderedSections,
}: {
  orderedSections: { key: string; section: WikiSection | null }[];
}) {
  return (
    <nav className="sticky top-20 self-start text-sm">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">목차</p>
      <ul className="space-y-1">
        {orderedSections.map(({ key, section }) => (
          <li key={key}>
            <a
              href={`#${key}`}
              className={`flex items-center justify-between rounded px-2 py-1 ${
                section ? 'hover:bg-slate-100' : 'text-slate-400'
              }`}
            >
              <span>{SECTION_LABELS[key] ?? key}</span>
              {section ? (
                <span className="text-[10px] text-slate-400">v{(section as WikiSection).sectionVersion ?? '—'}</span>
              ) : (
                <span className="text-[10px] text-slate-300">—</span>
              )}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * v2.0 Step 3 — broker_views section 의 verification badge.
 * wiki 표 의 broker count vs D1 의 latest broker count (6mo dedup) 비교.
 * mismatch (wiki < D1) → 빨강 (simplification). 일치 → 회색.
 */
function BrokerViewsBadge({
  meta,
}: {
  meta: { d1_broker_count: number; d1_snapshot_count: number; wiki_broker_count: number };
}) {
  const mismatch = meta.wiki_broker_count < meta.d1_broker_count;
  const cls = mismatch
    ? 'rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700'
    : 'rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600';
  return (
    <span
      className={cls}
      title={`wiki 표 ${meta.wiki_broker_count} broker / D1 latest dedup ${meta.d1_broker_count} broker (총 snapshot ${meta.d1_snapshot_count}, 6mo)${
        mismatch ? ' — simplification detected!' : ''
      }`}
    >
      📊 {meta.wiki_broker_count}/{meta.d1_broker_count} broker
    </span>
  );
}

function Content({
  symbol,
  orderedSections,
  referencedChunks,
  risks,
  financials,
  peerComparisons,
  operational,
}: {
  symbol: string;
  orderedSections: { key: string; section: WikiSection | null }[];
  referencedChunks: Record<string, ReferencedChunk>;
  risks: import('../lib/api').Risk[];
  financials: import('../lib/api').FinancialRow[];
  peerComparisons: import('../lib/api').PeerRow[];
  operational: import('../lib/api').OperationalRow[];
}) {
  const components = {
    a: ({ href, children, ...rest }: { href?: string; children?: React.ReactNode }) => {
      if (href && href.startsWith('#cite-')) {
        const chunkId = href.slice('#cite-'.length);
        return <CitationMark chunkId={chunkId} chunk={referencedChunks[chunkId]}>{children}</CitationMark>;
      }
      return (
        <a href={href} target="_blank" rel="noreferrer" {...rest}>
          {children}
        </a>
      );
    },
  };
  return (
    <article className="space-y-8">
      {orderedSections.map(({ key, section }) => (
        <Fragment key={key}>
          {/* Phase v3.5b — catalysts section 직전 의 D1 source 분포 panel. */}
          {key === 'catalysts' && <CatalystSourcesPanel symbol={symbol} />}
          {/* Phase v3.5a — risks section 직전 의 D1 panel (severity sorted). */}
          {key === 'risks' && risks.length > 0 && <RisksPanel risks={risks} />}
          {/* Phase v3.5a Phase 2 — financials section 직전 의 D1 matrix (broker × year). */}
          {key === 'financials' && financials.length > 0 && <FinancialsMatrix rows={financials} />}
          {/* Phase v3.5a Phase 3 — valuation section 직전 의 peer comparison panel. */}
          {key === 'valuation' && peerComparisons.length > 0 && <PeerPanel rows={peerComparisons} />}
          {/* Phase v3.5a Phase 3 — segments section 직전 의 operational metrics panel. */}
          {key === 'segments' && operational.length > 0 && <OperationalPanel rows={operational} />}
        <section id={key} className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-baseline gap-2">
            <h2 className="text-lg font-semibold tracking-tight">
              {SECTION_LABELS[key] ?? key}
            </h2>
            {key === 'broker_views' && section?.meta && (
              <BrokerViewsBadge meta={section.meta} />
            )}
          </div>
          {section ? (
            <div className="prose prose-slate prose-sm max-w-none prose-headings:font-semibold prose-headings:text-slate-900 prose-h3:text-base prose-h3:mt-5 prose-h4:text-sm prose-h4:mt-4 prose-p:text-slate-700 prose-strong:text-slate-900 prose-table:text-xs prose-thead:bg-slate-50 prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1 prose-li:my-0.5 prose-ul:my-2 prose-ol:my-2 prose-code:text-xs prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded">
              <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]} components={components}>
                {preprocessCitations(section.content)}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-slate-400">아직 데이터가 없습니다. 리포트가 수집되면 자동 생성됩니다.</p>
          )}
          {section && section.citations && section.citations.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              인용 {section.citations.length}개
            </p>
          )}
        </section>
        </Fragment>
      ))}
    </article>
  );
}

/**
 * Phase v3.5a Phase 2 — Financials D1 matrix.
 * broker × year, OP/OPM/EPS/PER/ROE.
 * Decision 2a — wiki content 의 LLM 합성 표 제거 후 본 panel 이 truth source.
 */
function FinancialsMatrix({ rows }: { rows: import('../lib/api').FinancialRow[] }) {
  const fmtInt = (n: number | null): string => (n === null ? '—' : n.toLocaleString());
  const fmtPct = (n: number | null): string => (n === null ? '—' : `${n.toFixed(1)}%`);
  const fmtX = (n: number | null): string => (n === null ? '—' : `${n.toFixed(1)}x`);
  return (
    <section className="rounded-lg border border-cyan-300 bg-cyan-50 p-4">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-base font-semibold tracking-tight text-cyan-900">
          📊 Financials matrix
        </h2>
        <span className="text-xs text-cyan-700">
          D1 · broker × year · 최근 6개월 · {rows.length} rows
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-cyan-100/50 text-cyan-900">
            <tr>
              <th className="px-2 py-1 text-left">broker</th>
              <th className="px-2 py-1 text-left">year</th>
              <th className="px-2 py-1 text-right">매출 (십억)</th>
              <th className="px-2 py-1 text-right">영업이익 (십억)</th>
              <th className="px-2 py-1 text-right">OPM</th>
              <th className="px-2 py-1 text-right">EPS (원)</th>
              <th className="px-2 py-1 text-right">PER</th>
              <th className="px-2 py-1 text-right">ROE</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.broker}-${r.year}-${i}`} className="border-b border-cyan-200/60 last:border-b-0">
                <td className="px-2 py-1 text-slate-700">{r.broker}</td>
                <td className="px-2 py-1 font-mono text-slate-600">{r.year}</td>
                <td className="px-2 py-1 text-right font-variant-numeric tabular-nums">{fmtInt(r.revenue)}</td>
                <td className="px-2 py-1 text-right font-variant-numeric tabular-nums">{fmtInt(r.op_income)}</td>
                <td className="px-2 py-1 text-right font-variant-numeric tabular-nums">{fmtPct(r.op_margin)}</td>
                <td className="px-2 py-1 text-right font-variant-numeric tabular-nums">{fmtInt(r.eps)}</td>
                <td className="px-2 py-1 text-right font-variant-numeric tabular-nums">{fmtX(r.per)}</td>
                <td className="px-2 py-1 text-right font-variant-numeric tabular-nums">{fmtPct(r.roe)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Phase v3.5a Phase 3 — Peer comparison D1 panel.
 * peer × metric_type × our/peer/ratio + note.
 * 1A placement — valuation wiki section 의 직전.
 */
function PeerPanel({ rows }: { rows: import('../lib/api').PeerRow[] }) {
  const fmt = (n: number | null, suffix = ''): string =>
    n === null ? '—' : `${n}${suffix}`;
  return (
    <section className="rounded-lg border border-violet-300 bg-violet-50 p-4">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-base font-semibold tracking-tight text-violet-900">
          ⚖️ Peer comparison
        </h2>
        <span className="text-xs text-violet-700">D1 · 최근 6개월 · {rows.length} rows</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-violet-100/50 text-violet-900">
            <tr>
              <th className="px-2 py-1 text-left">peer</th>
              <th className="px-2 py-1 text-left">metric</th>
              <th className="px-2 py-1 text-right">our</th>
              <th className="px-2 py-1 text-right">peer</th>
              <th className="px-2 py-1 text-right">ratio</th>
              <th className="px-2 py-1 text-left">note</th>
              <th className="px-2 py-1 text-right">date</th>
              <th className="px-2 py-1 text-right">broker</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-violet-200/60 last:border-b-0">
                <td className="px-2 py-1 text-slate-700">{r.peer_name}</td>
                <td className="px-2 py-1 font-mono text-slate-600">{r.metric_type}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt(r.our_value)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt(r.peer_value)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.ratio !== null ? `${r.ratio.toFixed(2)}x` : '—'}</td>
                <td className="px-2 py-1 text-slate-600">{r.note ?? '—'}</td>
                <td className="px-2 py-1 text-right font-mono text-slate-400">{r.dated_at}</td>
                <td className="px-2 py-1 text-right text-slate-500">{r.broker}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Phase v3.5a Phase 3 — Operational metrics D1 panel.
 * period × metric_type × segment × value + qoq/yoy + broker.
 * 1A placement — segments wiki section 의 직전.
 */
const OPERATIONAL_METRIC_LABELS: Record<string, string> = {
  asp: '평균판매단가 (ASP)',
  b_g: '비트 그로스 (B/G)',
  shipment: '출하량',
  capa: '생산능력 (Capa)',
  mix_pct: '비중',
  op_margin: '영업이익률',
  op_income: '영업이익',
  revenue: '매출',
  utilization: '가동률',
  yield: '수율',
};

function OperationalPanel({ rows }: { rows: import('../lib/api').OperationalRow[] }) {
  const fmtChange = (n: number | null): string => (n === null ? '—' : `${n > 0 ? '+' : ''}${n}%`);
  const labelMetric = (raw: string): string => OPERATIONAL_METRIC_LABELS[raw.toLowerCase()] ?? raw;
  return (
    <section className="rounded-lg border border-emerald-300 bg-emerald-50 p-4">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-base font-semibold tracking-tight text-emerald-900">
          📈 Operational metrics
        </h2>
        <span className="text-xs text-emerald-700">D1 · 최근 6개월 · {rows.length} rows</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-emerald-100/50 text-emerald-900">
            <tr>
              <th className="px-2 py-1 text-left">period</th>
              <th className="px-2 py-1 text-left">segment</th>
              <th className="px-2 py-1 text-left">metric</th>
              <th className="px-2 py-1 text-right">value</th>
              <th className="px-2 py-1 text-left">unit</th>
              <th className="px-2 py-1 text-right">QoQ</th>
              <th className="px-2 py-1 text-right">YoY</th>
              <th className="px-2 py-1 text-right">date</th>
              <th className="px-2 py-1 text-right">broker</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-emerald-200/60 last:border-b-0">
                <td className="px-2 py-1 font-mono text-slate-600">{r.period}</td>
                <td className="px-2 py-1 text-slate-700">{r.segment ?? '—'}</td>
                <td className="px-2 py-1 text-slate-600">{labelMetric(r.metric_type)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.value.toLocaleString()}</td>
                <td className="px-2 py-1 text-slate-500">{r.unit}</td>
                <td className="px-2 py-1 text-right tabular-nums text-slate-600">{fmtChange(r.qoq_change)}</td>
                <td className="px-2 py-1 text-right tabular-nums text-slate-600">{fmtChange(r.yoy_change)}</td>
                <td className="px-2 py-1 text-right font-mono text-slate-400">{r.dated_at}</td>
                <td className="px-2 py-1 text-right text-slate-500">{r.broker}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Phase v3.5a — Risks D1 panel.
 * severity 정렬 (D1 query 의 sort by severity DESC).
 * 1A placement — risks wiki section 의 직전.
 */
/**
 * Phase v3.5b — catalysts wiki section 직전. D1 catalysts dedup raw + source broker chip.
 * Wiki LLM 의 grouped narrative 와 평행 — D1 dedup 의 1:1 source 시각화.
 */
function CatalystSourcesPanel({ symbol }: { symbol: string }) {
  const { data, loading } = useAsync(() => api.catalysts(symbol), [symbol]);
  const [open, setOpen] = useState(true);
  if (loading || !data?.items.length) return null;

  // group by time_horizon (near → mid → long, null 은 'other')
  const horizonOrder: Array<{ key: 'near' | 'mid' | 'long' | 'other'; label: string; cls: string }> = [
    { key: 'near', label: '단기 (1–3개월)', cls: 'bg-amber-100 text-amber-800' },
    { key: 'mid', label: '중기 (3–12개월)', cls: 'bg-blue-100 text-blue-800' },
    { key: 'long', label: '장기 (1년 이상)', cls: 'bg-purple-100 text-purple-800' },
    { key: 'other', label: '기타', cls: 'bg-slate-100 text-slate-700' },
  ];
  const grouped = new Map<string, import('../lib/api').Catalyst[]>();
  for (const c of data.items) {
    const k = (c.timeHorizon ?? 'other') as 'near' | 'mid' | 'long' | 'other';
    if (!grouped.has(k)) grouped.set(k, []);
    grouped.get(k)!.push(c);
  }
  const totalCatalysts = data.items.length;
  const distinctBrokers = new Set<string>();
  for (const c of data.items) for (const s of c.sources) if (s.broker) distinctBrokers.add(s.broker);

  const dirIcon = (d: string | null) =>
    d === 'positive' ? { icon: '▲', cls: 'text-emerald-600' }
    : d === 'negative' ? { icon: '▼', cls: 'text-rose-600' }
    : { icon: '↔', cls: 'text-slate-500' };
  const convCls = (v: number | null) =>
    v == null ? 'bg-slate-100 text-slate-500'
    : v >= 70 ? 'bg-emerald-100 text-emerald-800'
    : v >= 40 ? 'bg-amber-100 text-amber-800'
    : 'bg-rose-100 text-rose-800';
  const fmtDate = (iso: string) => iso.slice(5).replace('-', '/'); // YYYY-MM-DD → MM/DD
  // broker short name — "신한투자증권" → "신한", "미래에셋증권" → "미래에셋", default 첫 6자
  const shortBroker = (b: string | null) => {
    if (!b) return '?';
    return b.replace(/투자증권|증권|투자|자산운용/g, '').slice(0, 6) || b.slice(0, 6);
  };

  return (
    <section className="rounded-lg border border-slate-300 bg-white">
      <header
        onClick={() => setOpen(!open)}
        className="flex cursor-pointer items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5 hover:bg-slate-100"
      >
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
          <span>📌</span>
          <span>카탈리스트 소스 분포</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span>D1 dedup · {totalCatalysts}건 · {distinctBrokers.size} broker 인용</span>
          <span className="text-slate-400">{open ? '▾' : '▸'}</span>
        </div>
      </header>
      {open && (
        <div className="px-4 py-3 space-y-4">
          {horizonOrder.map(({ key, label, cls }) => {
            const items = grouped.get(key);
            if (!items || items.length === 0) return null;
            return (
              <div key={key}>
                <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${cls}`}>{label}</span>
                <div className="mt-2 space-y-2.5">
                  {items.map((c) => {
                    const { icon, cls: dCls } = dirIcon(c.direction);
                    return (
                      <div key={c.id} className="py-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`w-4 text-center text-sm font-bold ${dCls}`}>{icon}</span>
                          <span className="text-sm font-medium text-slate-900">{c.title}</span>
                          {c.conviction != null && (
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${convCls(c.conviction)}`}>
                              conv {c.conviction}
                            </span>
                          )}
                        </div>
                        <div className="ml-6 mt-1 flex flex-wrap gap-1.5">
                          {c.sources.length === 0 ? (
                            <span className="text-[11px] text-slate-400">(source 없음)</span>
                          ) : (
                            c.sources.map((s) => (
                              <span
                                key={s.reportId}
                                title={`${s.broker ?? 'unknown'} · ${s.publishedAt}`}
                                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700 hover:border-slate-400 hover:bg-slate-100"
                              >
                                <span className="font-medium">{shortBroker(s.broker)}</span>
                                <span className="text-slate-400">{fmtDate(s.publishedAt)}</span>
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function RisksPanel({ risks }: { risks: import('../lib/api').Risk[] }) {
  return (
    <section className="rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="text-base font-semibold tracking-tight text-amber-900">
          ⚠️ Risks
        </h2>
        <span className="text-xs text-amber-700">D1 · severity sorted · 최근 6개월</span>
      </div>
      <ul className="space-y-1">
        {risks.map((r, i) => (
          <li key={i} className="flex items-start gap-2 border-b border-amber-200 py-1 last:border-b-0">
            <span
              className={`inline-block min-w-[28px] rounded px-1 text-center text-xs font-semibold ${
                r.severity >= 60
                  ? 'bg-rose-200 text-rose-900'
                  : r.severity >= 30
                  ? 'bg-yellow-200 text-yellow-900'
                  : 'bg-emerald-200 text-emerald-900'
              }`}
            >
              {r.severity}
            </span>
            {r.time_horizon && (
              <span className="min-w-[36px] text-center text-xs uppercase text-slate-500">
                {r.time_horizon}
              </span>
            )}
            <span className="flex-1 text-sm text-slate-700">{r.text}</span>
            <span className="text-xs text-slate-400 font-mono">{r.dated_at}</span>
            <span className="text-xs text-slate-500">{r.broker}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
